<template>
  <div class="cc-page" :class="{ 'cc-page--dark': isDark }">
    <header class="cc-top">
      <img class="cc-top__logo" :src="logoSrc" alt="AVA" />
      <div v-if="userLabel" class="cc-account">
        <span class="cc-account__prefix">Signed in as </span><b>{{ userLabel }}</b>
        <span v-if="companyName" class="cc-account__company"> · {{ companyName }}</span>
      </div>
    </header>

    <main class="cc-main">
      <section class="cc-card" :data-state="state" aria-live="polite">
        <div class="cc-handshake" aria-hidden="true">
          <div class="cc-mark"><span :class="['mdi', clientIcon]"></span></div>
          <div class="cc-link"><span class="cc-line"></span><span class="mdi mdi-link-variant"></span><span class="cc-line"></span></div>
          <div class="cc-mark"><img :src="logoSrc" alt="" /></div>
        </div>

        <!-- LOADING -->
        <div v-if="state === 'loading'">
          <div class="cc-skeleton" style="width: 70%; height: 22px"></div>
          <div class="cc-skeleton" style="width: 92%"></div>
          <div class="cc-skeleton" style="width: 84%"></div>
          <div class="cc-skeleton" style="width: 60%; margin-top: 24px"></div>
          <div class="cc-skeleton" style="width: 76%"></div>
          <span class="cc-sub cc-sub--spaced">Loading the connection request…</span>
        </div>

        <!-- ASK -->
        <div v-else-if="state === 'ask'">
          <h1>{{ displayName }} wants to use AVA as you</h1>
          <p class="cc-lede cc-lede--tight">
            {{ request.clientName || 'It' }} acts as you: it reads the AVA data you can open and asks in the chat before it
            changes anything.
          </p>

          <details class="cc-scope">
            <summary>What it can do</summary>
            <ul>
              <li><b>Reads</b> the AVA data you can open, and nothing else.</li>
              <li>
                <b>Creates and changes</b> records you're allowed to change, after asking in the chat: dashboards, composite
                sources, registers and rows, relationships, tags' fields, tasks, anomaly status and AI-finding reviews.
              </li>
              <li>
                <b>Can start a paid AI analysis</b> of an AVA Inspect item, only after you confirm: it sends the item's images
                to the AI provider and can replace that item's unreviewed AI findings.
              </li>
              <li>
                <b>Doesn't delete.</b> The one exception: rejecting an AI finding removes it for good, so it confirms that with
                you first.
              </li>
              <li v-if="isManagementStaff">
                <b>Switches company</b> to the other companies you can open, with your own account there.
              </li>
              <li><b>Sends</b> the AVA data it requests to {{ lowerName }}, so it can answer you.</li>
            </ul>
          </details>

          <dl class="cc-facts">
            <div class="cc-fact">
              <dt>App</dt>
              <dd>
                <span class="cc-host">{{ request.clientName || 'No name given' }}</span>
                <span v-if="request.verifiedHost" class="cc-sub">
                  <span class="cc-verified"><span class="mdi mdi-check-decagram" aria-hidden="true"></span>verified address</span>
                  {{ request.verifiedHost }}. The name comes from the app.
                </span>
                <span v-else class="cc-sub">
                  Registered itself with AVA. The name comes from the app and is not verified, so check where the sign-in goes.
                </span>
              </dd>
            </div>
            <div class="cc-fact">
              <dt>Sign-in goes to</dt>
              <dd>
                <span class="cc-host">{{ request.redirectIsLoopback ? `${request.redirectHost} · this computer` : request.redirectHost }}</span>
                <details class="cc-details">
                  <summary>Details</summary>
                  <span class="cc-sub">Full address: {{ request.redirectUri }}</span>
                </details>
              </dd>
            </div>
            <div class="cc-fact">
              <dt>Acting as</dt>
              <dd>
                <span class="cc-host">{{ request.userDisplayName || request.userEmail }}</span>
                <span v-if="actingAsDetail" class="cc-sub">{{ actingAsDetail }}</span>
              </dd>
            </div>
            <div class="cc-fact">
              <dt>Stays connected</dt>
              <dd>
                Until you disconnect it in
                <a v-if="activeSessionsPath" :href="activeSessionsPath" target="_blank" rel="noopener">Active sessions</a
                ><template v-else>Active sessions</template>, after {{ sessionLimits.idleDays }} days without use, or
                {{ sessionLimits.maxDays }} days at most.
              </dd>
            </div>
          </dl>

          <div v-if="request.redirectIsLoopback" class="cc-notice" role="note">
            <span class="mdi mdi-laptop" aria-hidden="true"></span>
            <div>
              <b>The sign-in goes to a program on this computer</b>
              Only allow this if you started connecting from {{ request.clientName || 'this app' }} on this computer in the last few
              minutes. Any program here could use the same name.
            </div>
          </div>

          <div v-if="decisionError" class="cc-notice cc-notice--error" role="alert">
            <span class="mdi mdi-alert-circle-outline" aria-hidden="true"></span>
            <div>
              <b>AVA couldn't record your answer</b>
              <template v-if="decisionErrorMessage">{{ decisionErrorMessage }}</template>
              <template v-else>Nothing was shared. Try again; if it keeps failing, start again from your AI assistant.</template>
            </div>
          </div>

          <div class="cc-actions">
            <button type="button" class="cc-btn cc-btn--secondary" :disabled="busy" @click="decide('deny')">Deny</button>
            <button type="button" class="cc-btn cc-btn--primary" :disabled="busy" @click="decide('approve')">
              <span v-if="busy === 'approve'" class="cc-spinner" aria-hidden="true"></span>
              <span>{{ busy === 'approve' ? 'Approving…' : 'Allow access' }}</span>
            </button>
          </div>

          <div class="cc-foot">
            <span>Not you? <a href="#" @click.prevent="switchAccount">Switch account</a></span>
            <span v-if="secondsLeft !== null">Request expires in <time>{{ countdown }}</time></span>
          </div>
        </div>

        <!-- EXPIRED -->
        <div v-else-if="state === 'expired'" class="cc-result">
          <span class="mdi mdi-timer-sand-complete cc-big cc-big--muted" aria-hidden="true"></span>
          <h1>This request is no longer open</h1>
          <p class="cc-lede">
            It expired, or it was already answered, perhaps in another tab. Go back to your AI assistant; if AVA isn't
            connected there, start connecting again.
          </p>
          <div class="cc-actions">
            <router-link class="cc-btn cc-btn--secondary" to="/">Go to AVA</router-link>
          </div>
        </div>

        <!-- LOAD FAILED (network / server error: the request may still be open) -->
        <div v-else-if="state === 'load-failed'" class="cc-result">
          <span class="mdi mdi-cloud-alert-outline cc-big cc-big--muted" aria-hidden="true"></span>
          <h1>Couldn't load the request</h1>
          <p class="cc-lede">Something went wrong reaching AVA. Try again; if it keeps failing, start again from your AI assistant.</p>
          <div class="cc-actions">
            <router-link class="cc-btn cc-btn--secondary" to="/">Go to AVA</router-link>
            <button type="button" class="cc-btn cc-btn--primary" @click="load">Retry</button>
          </div>
        </div>

        <!-- APPROVED (the assistant finishes connecting after it exchanges the code) -->
        <div v-else-if="state === 'approved'" class="cc-result">
          <span class="mdi mdi-check-circle-outline cc-big cc-big--ok" aria-hidden="true"></span>
          <h1>Access approved</h1>
          <p class="cc-lede">
            Returning you to <b>{{ returnTarget }}</b> to finish connecting… If nothing happens, close this tab and go back to
            {{ lowerName }}.
          </p>
          <div class="cc-examples">
            <span class="cc-examples__title">Once {{ lowerName }} says AVA is connected, try asking:</span>
            <ul>
              <li v-for="question in examples" :key="question">“{{ question }}”</li>
            </ul>
          </div>
          <div v-if="redirectUrl" class="cc-actions">
            <button type="button" class="cc-btn cc-btn--primary" @click="followRedirect">Return now</button>
          </div>
          <p class="cc-sub cc-sub--spaced">
            Disconnect it anytime in
            <a v-if="activeSessionsPath" :href="activeSessionsPath" target="_blank" rel="noopener">Active sessions</a
            ><template v-else>Active sessions</template>.
          </p>
        </div>

        <!-- DENIED -->
        <div v-else-if="state === 'denied'" class="cc-result">
          <span class="mdi mdi-close-circle-outline cc-big cc-big--muted" aria-hidden="true"></span>
          <h1>You didn't connect {{ lowerName }}</h1>
          <p class="cc-lede">Nothing was shared. Returning you to <b>{{ returnTarget }}</b>, which will show that access was declined.</p>
        </div>

        <!-- MISSING REQUEST ID -->
        <div v-else class="cc-result">
          <span class="mdi mdi-link-variant-off cc-big cc-big--muted" aria-hidden="true"></span>
          <h1>No connection request</h1>
          <p class="cc-lede">Open this page from your AI assistant when it asks you to connect AVA.</p>
          <div class="cc-actions">
            <router-link class="cc-btn cc-btn--secondary" to="/">Go to AVA</router-link>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
  import connectAiService from '@Account/service/connectAi.service';
  import {
    MCP_SESSION_LIMITS,
    isRequestGone,
    loadFailureState,
    decisionFailure,
    exampleQuestions,
  } from '@Account/utils/connectAi';
  import { error as debugError } from '@/utils/debugLogger';

  // The browser follows the client's redirect a beat after the outcome renders, so the user sees it.
  // Approval waits longer so the first-question examples can be read ("Return now" skips the wait);
  // the single-use code it carries lives McpOAuth:AuthorizationCodeSeconds (default and maximum 60 s),
  // so keep this delay well under it.
  const REDIRECT_DELAY_MS = { approve: 5000, deny: 900 };

  export default {
    name: 'ConnectAi',
    data() {
      return {
        state: 'loading',
        request: null,
        busy: null,
        decisionError: false,
        decisionErrorMessage: null,
        secondsLeft: null,
        timer: null,
        redirectUrl: null,
        redirectTimer: null,
        sessionLimits: MCP_SESSION_LIMITS,
      };
    },
    computed: {
      requestId() {
        return typeof this.$route.query.request === 'string' ? this.$route.query.request : '';
      },
      isDark() {
        return this.$vuetify.theme.dark;
      },
      logoSrc() {
        return this.isDark ? '/img/AVA_Logo_Mint.svg' : '/img/AVA_Logo_CastletonGreen.svg';
      },
      currentUser() {
        return this.$store.getters['ACCOUNT/GET_USER'];
      },
      userLabel() {
        if (this.request) return this.request.userDisplayName || this.request.userEmail;
        const user = this.currentUser;
        return user && (user.displayName || user.email);
      },
      // The company the approval is bound to: the API issues the code for the signed-in account's company.
      companyName() {
        const company = this.$store.getters['ACCOUNT/GET_COMPANY'];
        return (company && company.name) || '';
      },
      isManagementStaff() {
        return !!this.$store.getters['ACCOUNT/IS_ADMIN'];
      },
      actingAsDetail() {
        const email = this.request && this.request.userDisplayName ? this.request.userEmail : '';
        return [email, this.companyName].filter(Boolean).join(' · ');
      },
      // Active sessions live on the user's own profile page (UserProfile.vue renders ActiveSessions for the owner).
      activeSessionsPath() {
        const user = this.currentUser;
        return user && user.id ? `/users/${encodeURIComponent(user.id)}` : '';
      },
      examples() {
        const facility = this.$store.getters['FACILITY/GET_FACILITY'];
        return exampleQuestions(facility && facility.name);
      },
      displayName() {
        return (this.request && this.request.clientName) || 'An AI assistant';
      },
      lowerName() {
        return (this.request && this.request.clientName) || 'the AI assistant';
      },
      clientIcon() {
        return this.request && this.request.redirectIsLoopback ? 'mdi-console' : 'mdi-web';
      },
      returnTarget() {
        if (!this.request) return '';
        return this.request.redirectIsLoopback ? this.lowerName : this.request.redirectHost;
      },
      countdown() {
        const s = this.secondsLeft || 0;
        return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
      },
    },
    mounted() {
      if (!this.requestId) {
        this.state = 'missing';
        return;
      }
      this.load();
    },
    beforeDestroy() {
      this.stopCountdown();
      if (this.redirectTimer) clearTimeout(this.redirectTimer);
    },
    methods: {
      // Also the Retry of the load-failed state: a temporary failure leaves the request open.
      async load() {
        this.stopCountdown();
        this.state = 'loading';
        try {
          const res = await connectAiService.getRequest(this.requestId);
          this.request = res.data;
          this.state = 'ask';
          this.startCountdown(this.request.expiresAt);
        } catch (err) {
          if (!isRequestGone(err)) debugError('[ConnectAi] loading the connection request failed', err);
          this.state = loadFailureState(err);
        }
      },
      startCountdown(expiresAt) {
        const end = Date.parse(expiresAt);
        if (Number.isNaN(end)) return;
        const tick = () => {
          this.secondsLeft = Math.max(0, Math.round((end - Date.now()) / 1000));
          if (this.secondsLeft === 0) {
            this.stopCountdown();
            if (this.state === 'ask') this.state = 'expired';
          }
        };
        tick();
        this.timer = setInterval(tick, 1000);
      },
      stopCountdown() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      },
      async decide(decision) {
        this.busy = decision;
        this.decisionError = false;
        this.decisionErrorMessage = null;
        try {
          const res = decision === 'approve'
            ? await connectAiService.approve(this.requestId)
            : await connectAiService.deny(this.requestId);
          this.stopCountdown();
          this.state = decision === 'approve' ? 'approved' : 'denied';
          this.redirectUrl = (res.data && res.data.redirectUrl) || null;
          if (this.redirectUrl) this.redirectTimer = setTimeout(this.followRedirect, REDIRECT_DELAY_MS[decision]);
        } catch (err) {
          const failure = decisionFailure(err);
          if (failure.state === 'expired') {
            this.stopCountdown();
            this.state = 'expired';
          } else {
            debugError(`[ConnectAi] ${decision} failed`, err);
            this.decisionError = true;
            this.decisionErrorMessage = failure.message;
          }
        } finally {
          this.busy = null;
        }
      },
      followRedirect() {
        if (this.redirectTimer) clearTimeout(this.redirectTimer);
        this.redirectTimer = null;
        if (this.redirectUrl) window.location.assign(this.redirectUrl);
      },
      switchAccount() {
        // LOGOUT reloads this URL signed out; the route's returnAfterLogin brings the next
        // sign-in back to this same request.
        this.$store.dispatch('ACCOUNT/LOGOUT');
      },
    },
  };
</script>

<style lang="scss" scoped>
  // Tokens mirror the approved showcase (src/assets/showcase/connect-ai-assistant-consent.html).
  .cc-page {
    --cc-bg-app: #f3f3f3;
    --cc-bg-card: #fcfdfc;
    --cc-bg-subtle: #f0f2f1;
    --cc-border: rgba(0, 0, 0, 0.1);
    --cc-border-strong: rgba(0, 0, 0, 0.22);
    --cc-text: #1f2422;
    --cc-text-secondary: #55605c;
    --cc-text-muted: #6d7773;
    --cc-primary: var(--ds-brand-castleton, #03624c);
    --cc-primary-hover: #024d3c;
    --cc-on-primary: #ffffff;
    --cc-warn-bg: #fff6dc;
    --cc-warn-border: #e9c25a;
    --cc-warn-text: #5c4300;
    --cc-warn-icon: #9a6b00;
    --cc-error-bg: #fdeceb;
    --cc-error-text: #8e1515;
    --cc-shadow: 0 1px 2px rgba(16, 24, 22, 0.06), 0 8px 24px -8px rgba(16, 24, 22, 0.14);

    min-height: 100vh;
    background: var(--cc-bg-app);
    color: var(--cc-text);
    font-family: var(--ds-font-family-body, 'IBM Plex Sans', 'Roboto', sans-serif);
    font-size: var(--ds-font-body, 14px);
    line-height: 1.5;

    a {
      color: var(--cc-primary);
      text-underline-offset: 3px;
    }
  }

  .cc-page--dark {
    --cc-bg-app: #202020;
    --cc-bg-card: #171816;
    --cc-bg-subtle: #242624;
    --cc-border: rgba(255, 255, 255, 0.1);
    --cc-border-strong: rgba(255, 255, 255, 0.24);
    --cc-text: #f2f4f3;
    --cc-text-secondary: #b9c2be;
    --cc-text-muted: #9aa4a0;
    --cc-primary: var(--ds-brand-mint, #2cc295); // Castleton fails contrast on dark surfaces
    --cc-primary-hover: #4fd3ab;
    --cc-on-primary: #04241b;
    --cc-warn-bg: #2e2612;
    --cc-warn-border: #6b5410;
    --cc-warn-text: #f3dc9b;
    --cc-warn-icon: #fbbd1c;
    --cc-error-bg: #341716;
    --cc-error-text: #f4b4b0;
    --cc-shadow: 0 1px 2px rgba(0, 0, 0, 0.4), 0 12px 32px -12px rgba(0, 0, 0, 0.6);
  }

  .cc-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 960px;
    margin: 0 auto;
    padding: 16px 16px 0;
  }
  .cc-top__logo {
    height: 28px;
    width: auto;
  }
  .cc-account {
    font-size: 13px;
    color: var(--cc-text-secondary);
    text-align: right;
    b {
      color: var(--cc-text);
      font-weight: 600;
    }
  }
  .cc-account__company {
    color: var(--cc-text-secondary);
  }

  .cc-main {
    padding: 32px 16px 64px;
  }
  .cc-card {
    max-width: 520px;
    margin: 0 auto;
    background: var(--cc-bg-card);
    border-radius: 12px;
    box-shadow: var(--cc-shadow);
    padding: 32px;
  }

  // The handshake: two marks joined by two line segments, the link icon sitting in the gap.
  .cc-handshake {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
  }
  .cc-mark {
    width: 48px;
    height: 48px;
    flex: none;
    display: grid;
    place-items: center;
    border-radius: 8px;
    background: var(--cc-bg-subtle);
    color: var(--cc-text-secondary);
    font-size: 24px;
    img {
      width: 30px;
      height: auto;
    }
  }
  .cc-link {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    .mdi {
      flex: none;
      width: 24px;
      height: 24px;
      display: grid;
      place-items: center;
      color: var(--cc-text-muted);
      font-size: 18px;
      line-height: 1;
      transition: color 300ms ease 300ms;
    }
  }
  .cc-line {
    position: relative;
    flex: 1;
    height: 2px;
    border-radius: 2px;
    background: var(--cc-border);
    overflow: hidden;
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: var(--cc-primary);
      transform: scaleX(0);
      transform-origin: left center;
      transition: transform 350ms cubic-bezier(0.16, 1, 0.3, 1);
    }
  }
  .cc-line + .mdi + .cc-line::after {
    transition-delay: 350ms;
  }
  [data-state='approved'] {
    .cc-line::after {
      transform: scaleX(1);
    }
    .cc-link .mdi {
      color: var(--cc-primary);
    }
  }
  [data-state='denied'] .cc-link,
  [data-state='expired'] .cc-link,
  [data-state='load-failed'] .cc-link {
    opacity: 0.5;
  }

  h1 {
    font-family: var(--ds-font-family-heading, 'Poppins', 'Roboto', sans-serif);
    font-size: 24px;
    line-height: 1.25;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0 0 8px;
    text-wrap: balance;
  }
  .cc-lede {
    color: var(--cc-text-secondary);
    margin: 0 0 24px;
    max-width: 62ch;
  }
  .cc-lede--tight {
    margin-bottom: 8px;
  }

  // Native disclosures: "What it can do" under the lede, "Details" under the redirect host.
  .cc-scope,
  .cc-details {
    summary {
      cursor: pointer;
      color: var(--cc-primary);
      font-weight: 600;
      width: fit-content;
      border-radius: 4px;
      &:focus-visible {
        outline: 2px solid var(--cc-primary);
        outline-offset: 2px;
      }
    }
  }
  .cc-scope {
    margin: 0 0 24px;
    summary {
      font-size: 14px;
    }
    ul {
      margin: 8px 0 0;
      padding-left: 18px;
      color: var(--cc-text-secondary);
      font-size: 13px;
    }
    li + li {
      margin-top: 4px;
    }
    b {
      color: var(--cc-text);
      font-weight: 600;
    }
  }
  .cc-details {
    margin-top: 2px;
    summary {
      font-size: 12px;
    }
  }

  // First-question examples on the approved state: a centered block, its lines read left to right.
  .cc-examples {
    margin: 0 auto 24px;
    padding: 12px 16px;
    border-radius: 8px;
    background: var(--cc-bg-subtle);
    text-align: left;
    font-size: 13px;
    max-width: 100%;
    ul {
      margin: 6px 0 0;
      padding-left: 18px;
    }
    li + li {
      margin-top: 2px;
    }
  }
  .cc-examples__title {
    color: var(--cc-text-secondary);
  }

  .cc-facts {
    margin: 0 0 24px;
    padding: 0;
    border-top: 1px solid var(--cc-border);
  }
  .cc-fact {
    display: grid;
    grid-template-columns: 132px 1fr;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid var(--cc-border);
    dt {
      color: var(--cc-text-muted);
      font-size: 13px;
      padding-top: 1px;
    }
    dd {
      margin: 0;
      min-width: 0;
    }
  }
  .cc-host {
    font-weight: 600;
    font-size: 15px;
    overflow-wrap: anywhere;
  }
  .cc-sub {
    display: block;
    color: var(--cc-text-secondary);
    font-size: 13px;
    margin-top: 2px;
    overflow-wrap: anywhere;
  }
  .cc-sub--spaced {
    margin-top: 16px;
  }
  .cc-verified {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-right: 4px;
    color: var(--cc-primary);
    font-size: 12px;
    font-weight: 600;
  }

  .cc-notice {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 12px 16px;
    border-radius: 8px;
    background: var(--cc-warn-bg);
    border: 1px solid var(--cc-warn-border);
    color: var(--cc-warn-text);
    font-size: 13px;
    margin: 0 0 24px;
    .mdi {
      font-size: 20px;
      line-height: 1;
      color: var(--cc-warn-icon);
      margin-top: 1px;
    }
    b {
      display: block;
      font-weight: 600;
      margin-bottom: 2px;
    }
  }
  .cc-notice--error {
    background: var(--cc-error-bg);
    border-color: transparent;
    color: var(--cc-error-text);
    .mdi {
      color: var(--cc-error-text);
    }
  }

  .cc-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    align-items: center;
  }
  .cc-btn {
    font: inherit;
    font-weight: 600;
    font-size: 14px;
    min-height: 40px;
    padding: 0 20px;
    border-radius: 8px;
    border: 1px solid transparent;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-decoration: none;
    transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
    &:focus-visible {
      outline: 2px solid var(--cc-primary);
      outline-offset: 2px;
    }
  }
  .cc-btn--primary {
    background: var(--cc-primary);
    color: var(--cc-on-primary);
    &:hover:not(:disabled) {
      background: var(--cc-primary-hover);
    }
  }
  .cc-btn--secondary {
    background: transparent;
    color: var(--cc-text);
    border-color: var(--cc-border-strong);
    &:hover:not(:disabled) {
      background: var(--cc-bg-subtle);
    }
  }
  .cc-spinner {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid currentColor;
    border-right-color: transparent;
    animation: cc-spin 700ms linear infinite;
  }
  @keyframes cc-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .cc-foot {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid var(--cc-border);
    color: var(--cc-text-muted);
    font-size: 12px;
    time {
      font-variant-numeric: tabular-nums;
    }
  }

  // Outcome states (approved, denied, expired, load-failed, missing) are one centered message.
  .cc-result {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    .cc-lede {
      margin-left: auto;
      margin-right: auto;
    }
    .cc-actions {
      justify-content: center;
    }
  }
  .cc-big {
    font-size: 40px;
    line-height: 1;
    margin-bottom: 16px;
    display: block;
  }
  .cc-big--ok {
    color: var(--cc-primary);
  }
  .cc-big--muted {
    color: var(--cc-text-muted);
  }

  .cc-skeleton {
    height: 14px;
    border-radius: 4px;
    background: var(--cc-bg-subtle);
    margin: 10px 0;
    animation: cc-pulse 1.4s ease-in-out infinite;
  }
  @keyframes cc-pulse {
    50% {
      opacity: 0.55;
    }
  }

  @media (max-width: 560px) {
    .cc-main {
      padding-top: 16px;
    }
    .cc-card {
      padding: 24px 16px;
    }
    .cc-fact {
      grid-template-columns: 1fr;
      gap: 2px;
    }
    .cc-actions {
      flex-direction: column-reverse;
      align-items: stretch;
    }
    .cc-account__prefix {
      display: none;
    }
    h1 {
      font-size: 20px;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .cc-page *,
    .cc-page *::after {
      transition-duration: 0ms !important;
      animation: none !important;
    }
  }
</style>
